import * as React from 'react';

import {
  Table,
  TableTopBar,
  BasicSingleCell,
  type GenericHeaderItems,
  type GenericObject,
} from '@spaced-out/ui-design-system/lib/components/Table';
import {
  BodyMediumBold,
  ButtonTextSmall,
  TEXT_COLORS,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {
  ButtonDropdown,
  SimpleButtonDropdown,
} from '@spaced-out/ui-design-system/lib/components/ButtonDropdown';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu';
import {TruncatedTextWithTooltip} from '@spaced-out/ui-design-system/lib/components/TruncatedTextWithTooltip';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {borderRadiusNone} from '@spaced-out/ui-design-system/lib/styles/variables/_border';
import {classify} from 'src/utils/classify';

import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useAgencyData} from 'src/hooks/useAgencyData';
import {useTableManager} from 'src/hooks/useTableManager';
import {formatToCustomDateString} from 'src/utils/analytics-genesis';
import FilterDropDown from 'src/components/analytics-genesis/journeys-dashboard/common/filter-drop-down';
import AllTableFooter from 'src/components/analytics-genesis/journeys-dashboard/common/all-table-footer';
import {DEFAULT_PAGE_SIZE} from 'src/components/analytics-genesis/byod/constants';
import {htmlToPlainText} from 'src/components/analytics-genesis/byod/utils/html-to-text';
import {
  ByodTableFilter,
  EMPTY_TABLE_SEARCH,
} from 'src/components/analytics-genesis/byod/components/byod-table-filter';
import type {TableSearch} from 'src/components/analytics-genesis/byod/components/byod-table-filter';
import {resolveSearchableColumns} from 'src/components/analytics-genesis/byod/utils/searchable-columns';
import type {SearchableColumn} from 'src/types/byod';

import css from './byod-table.css';

// Truncates a header label to one line with a tooltip on hover — only shown
// when the label actually overflows (TruncatedTextWithTooltip's own check).
function HeaderLabel({label}: {label: string}) {
  return (
    <TruncatedTextWithTooltip tooltip={{bodyMaxLines: 5}}>
      {label}
    </TruncatedTextWithTooltip>
  );
}

export type TableColumn = {key: string; label: string};

export type FetchDataParams = {
  offset: number;
  limit: number;
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
};

export type ByodTableProps = {
  /** Widget display name shown in the table header. */
  label?: string;
  columns: TableColumn[];
  rows: Record<string, unknown>[];
  totalCount: number;
  /** Currently selected columns (from widget_config_json). null = show all. */
  selectedColumns: string[] | null;
  onSelectedColumnsChange?: (columns: string[]) => void;
  isSavingColumns?: boolean;

  /**
   * Entity-attribute columns the user can pick under the "Entity Variable"
   * section — the `all_attributes_columns` union from the response. Plus the
   * currently-selected subset (persisted in widget_config_json.entities_columns)
   * and its change handler. Omit `onSelectedEntityColumnsChange` to hide the
   * entity-variable affordance entirely (e.g. preview / report read-only paths).
   */
  entityColumns?: string[];
  selectedEntityColumns?: string[] | null;
  onSelectedEntityColumnsChange?: (columns: string[]) => void;

  /**
   * Initial sort (restored from persisted widget_config_json).
   * ByodTable owns sort state after mount.
   */
  initialSort?: {sortBy: string; sortOrder: 'asc' | 'desc' | 'original'};

  /**
   * Single callback — fires on any state change the parent must re-fetch for
   * (page change, sort change). Omit to disable pagination/sort entirely
   * (e.g. static preview).
   */
  onFetchData?: (params: FetchDataParams) => void;

  pageSize?: number;
  isLoading?: boolean;

  /**
   * Increment to reset pagination to page 1 (e.g. when external filters change).
   * The table resets its internal page number whenever this value changes.
   */
  resetKey?: number;

  /** Three-dot menu actions. */
  onEdit?: () => void;
  onRemove?: () => void;
  isRemoving?: boolean;

  /** Export button — omit to hide. */
  onExport?: () => void;

  /** Schedule-report button — omit to hide. */
  onSchedule?: () => void;

  /**
   * View-time search. The applied `{search_column?, search_text}` drives the
   * "Filter" button's active state and seeds its panel. Omit `onSearchChange`
   * to hide the Filter button entirely (e.g. the chart's static data table).
   * The surface owns re-querying (forwards it into `widget_config`).
   */
  search?: TableSearch;
  onSearchChange?: (search: TableSearch) => void;
  /**
   * Columns that may be filtered on (`table_config.searchable_columns`). The
   * Filter button appears ONLY when this resolves to ≥1 column (and
   * `onSearchChange` is set), and offers exactly these columns as fields.
   */
  searchableColumns?: SearchableColumn[];

  /**
   * Optional class overrides. `wrapper` augments the table's scroll
   * container (composed with the default) — e.g. to cap height with a
   * `max-height` + `overflow-y` so the table doesn't grow to fill its parent.
   */
  classNames?: {wrapper?: string};
};

export function ByodTable({
  label,
  columns,
  rows,
  totalCount,
  selectedColumns,
  onSelectedColumnsChange,
  isSavingColumns = false,
  entityColumns,
  selectedEntityColumns,
  onSelectedEntityColumnsChange,
  initialSort,
  onFetchData,
  pageSize = DEFAULT_PAGE_SIZE,
  isLoading = false,
  resetKey = 0,
  onEdit,
  onRemove,
  isRemoving = false,
  onExport,
  onSchedule,
  search,
  onSearchChange,
  searchableColumns,
  classNames,
}: ByodTableProps) {
  const {t, language, dynamicTranslate} = useLabelTranslation();
  const agency = useAgencyData();
  const agencyTimezone = agency?.timezone;

  const {
    tableState,
    handlePageChange: tmPageChange,
    handleSortChange: tmSortChange,
  } = useTableManager({
    sort: initialSort ?? {sortBy: '', sortOrder: 'original'},
    pageNumber: 1,
  });

  // Reset to page 1 when external state changes (e.g. filters applied).
  const resetKeyRef = React.useRef(resetKey);
  React.useEffect(() => {
    if (resetKeyRef.current !== resetKey) {
      resetKeyRef.current = resetKey;
      tmPageChange(1);
    }
  }, [resetKey, tmPageChange]);

  // Ref prevents effect from firing when parent rebuilds the callback.
  const onFetchDataRef = React.useRef(onFetchData);
  onFetchDataRef.current = onFetchData;

  // Skip initial mount — parent handles the first load.
  const didMountRef = React.useRef(false);
  React.useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    const fetch = onFetchDataRef.current;
    if (!fetch) {
      return;
    }
    const offset = (tableState.pageNumber - 1) * pageSize;
    const params: FetchDataParams = {offset, limit: pageSize};
    if (tableState.sort.sortBy && tableState.sort.sortOrder !== 'original') {
      params.sort_by = tableState.sort.sortBy;
      params.sort_direction = tableState.sort.sortOrder;
    }
    fetch(params);
  }, [
    tableState.pageNumber,
    tableState.sort.sortBy,
    tableState.sort.sortOrder,
    pageSize,
  ]);

  const handlePageChange = React.useCallback(
    (page?: number | null) => {
      tmPageChange(page);
    },
    [tmPageChange],
  );

  const handleSort = React.useCallback(
    (sortKey: string, sortDirection: string) => {
      const sortOrder = (sortDirection as 'asc' | 'desc') || 'asc';
      tmSortChange(sortKey, sortOrder);
    },
    [tmSortChange],
  );

  const visibleColumns = React.useMemo(() => {
    if (!selectedColumns || selectedColumns.length === 0) {
      return columns;
    }
    const selectedSet = new Set(selectedColumns);
    return columns.filter((col) => selectedSet.has(col.key));
  }, [columns, selectedColumns]);

  // Two-mode column picker: fixed table columns vs entity-attribute columns
  // (mirrors the analytics-genesis people tables). The shared FilterDropDown
  // toggles between modes via `isEditingEntities`.
  const [isEditingEntities, setIsEditingEntities] = React.useState(false);
  // Offer the "Entity Variable" picker only when the widget is configured for
  // entity columns — signalled by the response carrying entity-attribute
  // metadata (`entityColumns` defined, even if empty). Non-entity widgets pass
  // `undefined` and never see the option.
  const supportsEntityColumns =
    Boolean(onSelectedEntityColumnsChange) && entityColumns != null;

  const columnMenuOptions = React.useMemo<MenuOption[]>(() => {
    const columnOptions: MenuOption[] = columns.map((col) => ({
      key: col.key,
      label: dynamicTranslate(col.label),
    }));
    // The "Entity Variable" row opens the entity-attribute sub-menu; shown at
    // the top of the menu, disabled when the response carries no entity
    // attributes.
    if (supportsEntityColumns) {
      return [
        {
          key: 'entityVariable',
          label: t('Entity_Variable', 'Entity Variable'),
          disabled: (entityColumns ?? []).length === 0,
        },
        ...columnOptions,
      ];
    }
    return columnOptions;
  }, [columns, dynamicTranslate, supportsEntityColumns, entityColumns, t]);

  const entitiesMenuColumns = React.useMemo<MenuOption[]>(
    () => (entityColumns ?? []).map((key) => ({key, label: key})),
    [entityColumns],
  );

  const selectedFilterKeys = React.useMemo(
    () =>
      selectedColumns && selectedColumns.length > 0
        ? selectedColumns
        : columns.map((c) => c.key),
    [selectedColumns, columns],
  );

  const showEntityOptionCheck =
    !isEditingEntities && (selectedEntityColumns ?? []).length > 0;

  // Routes saves to the active mode's handler. Used for both the main save and
  // FilterDropDown's interim save-on-mode-switch, so switching modes doesn't
  // lose the other mode's selection.
  const handleColumnSave = React.useCallback(
    async (keys: string[]) => {
      if (isEditingEntities) {
        onSelectedEntityColumnsChange?.(keys);
      } else {
        onSelectedColumnsChange?.(keys);
      }
    },
    [isEditingEntities, onSelectedColumnsChange, onSelectedEntityColumnsChange],
  );

  // Shared cell renderer for both fixed and entity-attribute columns — both read
  // their value off the row by key and get the same Decimal/array/date/number
  // formatting.
  const renderCell = React.useCallback(
    (colKey: string) =>
      ({
        data,
        className,
      }: {
        data: Record<string, unknown>;
        className?: string;
      }) => {
        const cellValue = data[colKey];
        const arrayItems = parseAsArray(cellValue);
        // parseAsArray returns [] (truthy) for empty arrays so they still
        // render via ArrayCell as N/A; null means the value isn't an array.
        if (arrayItems !== null) {
          return (
            <ArrayCell
              items={arrayItems}
              className={classify(className, css.tableHeaderColumn)}
            />
          );
        }
        // Scalar cells (numbers, text, and their N/A) stay left-aligned. Only
        // array cells are centered — handled in ArrayCell above, where the
        // value, single-item, and N/A states all center together.
        return (
          <BasicSingleCell
            className={classify(className, css.tableHeaderColumn)}
          >
            <span className={css.cellText}>
              <TruncatedTextWithTooltip tooltip={{bodyMaxLines: 20}}>
                {formatCellValue(cellValue, t, language, agencyTimezone)}
              </TruncatedTextWithTooltip>
            </span>
          </BasicSingleCell>
        );
      },
    [t, language, agencyTimezone],
  );

  // The sort arrow only appears on the actively-sorted column and takes up
  // its own horizontal space, shrinking what's left for the label — but it's
  // added/removed without the label's own text changing. TruncatedTextWithTooltip
  // only re-measures overflow when ITS OWN deps change (viewport width, its
  // children, `line`), not when a sibling icon's presence resizes it — so
  // toggling sort left it showing a stale (usually "not truncated") result:
  // truncation still LOOKED right (that part's pure CSS, always accurate) but
  // the tooltip wouldn't appear even though the text was now actually clipped.
  // Keying each label on the sort state forces a remount (and fresh
  // measurement) whenever the arrow's presence could have changed.
  const sortStateKey = `${tableState.sort.sortBy}-${tableState.sort.sortOrder}`;

  const headers = React.useMemo(() => {
    const fixedKeys = new Set(visibleColumns.map((c) => c.key));
    const fixedHeaders = visibleColumns.map((col) => ({
      key: col.key,
      // A long single-word label (e.g. an entity attribute path with no
      // spaces) has nowhere to wrap and overflows into the next header —
      // truncate it with a tooltip so the full label is still reachable.
      label: (
        <HeaderLabel key={sortStateKey} label={dynamicTranslate(col.label)} />
      ),
      sortable: Boolean(onFetchData),
      className: css.tableHeaderColumn,
      render: renderCell(col.key),
    }));
    // Selected entity-attribute columns, appended after the fixed columns. Never
    // sortable (the backend can't sort by a dynamic attribute); skip any that
    // collide with a fixed column key.
    const entityHeaders = (selectedEntityColumns ?? [])
      .filter((key) => !fixedKeys.has(key))
      .map((key) => ({
        key,
        label: <HeaderLabel key={sortStateKey} label={key} />,
        sortable: false,
        className: css.tableHeaderColumn,
        render: renderCell(key),
      }));
    return [...fixedHeaders, ...entityHeaders];
  }, [
    visibleColumns,
    onFetchData,
    dynamicTranslate,
    renderCell,
    selectedEntityColumns,
    sortStateKey,
  ]);

  // When the parent drives pagination (`onFetchData`), `rows` is already the
  // current page. Otherwise the component holds the full dataset and must
  // slice it client-side — without this the footer renders multiple pages but
  // the table shows every row and page changes do nothing (the broken case
  // for the chart's static data table).
  const pagedRows = React.useMemo(() => {
    if (onFetchData) {
      return rows;
    }
    const start = (tableState.pageNumber - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [onFetchData, rows, tableState.pageNumber, pageSize]);

  const entries = React.useMemo(
    () =>
      pagedRows.map((row, i) => ({
        ...row,
        _byod_row_id: `${tableState.pageNumber}-${i}`,
      })),
    [pagedRows, tableState.pageNumber],
  );

  const hasData = rows.length > 0;
  const effectiveTotalCount = onFetchData ? totalCount : rows.length;
  const totalPages = Math.max(1, Math.ceil(effectiveTotalCount / pageSize));
  React.useEffect(() => {
    if (!onFetchData && tableState.pageNumber > totalPages) {
      tmPageChange(1);
    }
  }, [onFetchData, tableState.pageNumber, totalPages, tmPageChange]);

  const menuOptions = React.useMemo(() => {
    const opts = [];
    if (onEdit) {
      opts.push({
        key: 'edit',
        label: t('Edit_widget', 'Edit widget'),
        iconLeft: 'pencil',
      });
    }
    if (onRemove) {
      opts.push({
        key: 'remove',
        label: t('Remove_widget', 'Remove widget'),
        iconLeft: 'trash',
      });
    }
    return opts;
  }, [onEdit, onRemove, t, language]);

  const handleMenuSelect = React.useCallback(
    (opt: {key: string}) => {
      if (opt.key === 'edit') {
        onEdit?.();
      } else if (opt.key === 'remove') {
        void onRemove?.();
      }
    },
    [onEdit, onRemove],
  );

  // Columns offered in the Filter panel's column scope — the backend-declared
  // `searchable_columns`, in order, with translated labels. Dimensions get a
  // text search; measures get a numeric comparison (the panel branches on type).
  const filterableColumns = React.useMemo(
    () =>
      resolveSearchableColumns(searchableColumns).map(({key, label, type}) => ({
        key,
        label: dynamicTranslate(label),
        type,
      })),
    [searchableColumns, dynamicTranslate],
  );

  return (
    <div
      className={css.byodTableContainer}
      style={{'--number-of-columns': headers.length} as React.CSSProperties}
    >
      <TableTopBar>
        <div className={css.headerLeft}>
          {label && <BodyMediumBold>{label}</BodyMediumBold>}
        </div>
        <div className={css.headerRight}>
          {onSearchChange && filterableColumns.length > 0 && (
            <ByodTableFilter
              columns={filterableColumns}
              value={search ?? EMPTY_TABLE_SEARCH}
              onApply={onSearchChange}
              buttonSize="small"
            />
          )}
          {onSchedule && (
            <Tooltip body={t('Schedule_report', 'Schedule report')}>
              <Button
                ariaLabel={t('Schedule_report', 'Schedule report')}
                iconRightName="bell"
                onClick={onSchedule}
                size="small"
                type="ghost"
              />
            </Tooltip>
          )}
          {onExport && (
            <Tooltip body={t('Export_Report', 'Export Report')}>
              <Button
                ariaLabel={t('Export_Overview_Data', 'Export Overview Data')}
                iconRightName="cloud-arrow-down"
                onClick={onExport}
                size="small"
                type="ghost"
              />
            </Tooltip>
          )}
          {onSelectedColumnsChange && columns.length > 0 && (
            <FilterDropDown
              headerTitle={
                isEditingEntities
                  ? t('Entity_variable', 'Entity variable')
                  : t('Edit_table_columns', 'Edit table columns')
              }
              menuOptions={
                isEditingEntities ? entitiesMenuColumns : columnMenuOptions
              }
              selectedFilterKeys={
                isEditingEntities
                  ? selectedEntityColumns ?? []
                  : selectedFilterKeys
              }
              handleOnSave={handleColumnSave}
              isSavingTableFilters={isSavingColumns}
              isEditingEntities={isEditingEntities}
              setIsEditingEntities={setIsEditingEntities}
              showEntityOptionCheck={showEntityOptionCheck}
              handleFilterSave={handleColumnSave}
              handleCancel={() => setIsEditingEntities(false)}
              // eslint-disable-next-line no-empty-function
              onSaveCallback={() => {}}
            />
          )}
          {menuOptions.length > 0 && !isRemoving && (
            <SimpleButtonDropdown
              type="ghost"
              size="small"
              iconLeftName="ellipsis-v"
              ariaLabel={t('Widget_actions', 'Widget actions')}
              options={menuOptions}
              onOptionSelect={handleMenuSelect}
              isFluid={false}
            />
          )}
        </div>
      </TableTopBar>

      <Table
        key="byod-table"
        borderRadius={borderRadiusNone}
        classNames={{
          wrapper: classify(css.tableWrapper, classNames?.wrapper),
        }}
        entries={entries}
        headers={
          headers as GenericHeaderItems<Record<string, unknown>, GenericObject>
        }
        idName="_byod_row_id"
        isLoading={isLoading}
        showHeader={hasData || isLoading}
        enableInternalSorting={!onFetchData}
        onSort={onFetchData ? handleSort : undefined}
        emptyText={
          <EmptyState
            title={t('No_result_found', 'No result found')}
            description={t(
              'No_data_available_for_the_selected_filters',
              'No data available for the selected filters.',
            )}
            imageVariant="data"
          />
        }
        stickyHeader
      />

      <AllTableFooter
        pageNumber={tableState.pageNumber}
        totalPages={totalPages}
        handlePageChange={handlePageChange}
        total={effectiveTotalCount}
        pageSize={pageSize}
      />
    </div>
  );
}

// Matches ISO 8601 date strings with or without timezone:
// 2026-01-15T10:30:00Z, 2026-01-15T10:30:00.534047, 2026-01-15T10:30:00+05:30
const ISO_DATE_RE =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?$/;

// Tries to extract an array from a cell value.
// Handles: native arrays, JSON-encoded array strings. Returns null if not an array.
function parseAsArray(value: unknown): MenuOption[] | null {
  let arr: unknown[] | null = null;
  if (Array.isArray(value)) {
    arr = value;
  } else if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const parsed: unknown = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          arr = parsed;
        }
      } catch {
        // not valid JSON
      }
    }
  }
  // Not an array at all → null so the caller falls back to formatCellValue.
  // An empty array stays an (empty) array so ArrayCell can render it as N/A.
  if (!arr) {
    return null;
  }
  return arr.map((item, i) => {
    if (item == null) {
      return {key: String(i), label: ''};
    }
    if (typeof item === 'object' && !Array.isArray(item)) {
      const obj = item as Record<string, unknown>;
      const nameKey = Object.keys(obj).find((k) => /name|label|title/i.test(k));
      const label =
        nameKey != null && obj[nameKey] != null
          ? String(obj[nameKey])
          : String(
              Object.values(obj).find((v) => typeof v === 'string') ??
                JSON.stringify(item),
            );
      // Use an id-like key if present, otherwise index
      const idKey = Object.keys(obj).find((k) => /id$/i.test(k));
      const key =
        idKey != null && obj[idKey] != null ? String(obj[idKey]) : String(i);
      return {key, label};
    }
    return {key: String(i), label: String(item)};
  });
}

// Generic dropdown cell for array values.
// 0 items → N/A, 1 item → label text, N items → "N items" dropdown.
function ArrayCell({
  items,
  className,
}: {
  items: MenuOption[];
  className?: string;
}) {
  const {t} = useLabelTranslation();

  if (items.length === 0) {
    return (
      <BasicSingleCell className={className}>
        <span className={classify(css.cellText, css.cellCentered)}>
          <TruncatedTextWithTooltip tooltip={{bodyMaxLines: 20}}>
            {t('NA', 'N/A')}
          </TruncatedTextWithTooltip>
        </span>
      </BasicSingleCell>
    );
  }

  if (items.length === 1) {
    return (
      <BasicSingleCell className={className}>
        <span className={classify(css.cellText, css.cellCentered)}>
          <TruncatedTextWithTooltip tooltip={{bodyMaxLines: 5}}>
            {items[0].label || t('NA', 'N/A')}
          </TruncatedTextWithTooltip>
        </span>
      </BasicSingleCell>
    );
  }

  return (
    <BasicSingleCell className={className}>
      <span className={css.cellCentered}>
        <ButtonDropdown
          menu={{
            options: items,
            size: 'medium',
            resolveLabel: (option: MenuOption) => (
              <ButtonTextSmall color={TEXT_COLORS.secondary}>
                <TruncatedTextWithTooltip tooltip={{bodyMaxLines: 5}}>
                  {option.label}
                </TruncatedTextWithTooltip>
              </ButtonTextSmall>
            ),
          }}
          type="ghost"
        >
          <ButtonTextSmall>
            <TruncatedTextWithTooltip tooltip={{bodyMaxLines: 5}}>
              {t('count_items', {
                count: items.length,
                defaultValue: '{{count}} items',
              })}
            </TruncatedTextWithTooltip>
          </ButtonTextSmall>
        </ButtonDropdown>
      </span>
    </BasicSingleCell>
  );
}

function formatCellValue(
  value: unknown,
  t: (key: string, fallback: string) => string,
  language?: string,
  agencyTimezone?: string,
): string {
  if (value == null) {
    return t('NA', 'N/A');
  }
  if (typeof value === 'number') {
    return value.toLocaleString(language);
  }
  if (typeof value === 'boolean') {
    return value ? t('Yes', 'Yes') : t('No', 'No');
  }
  if (typeof value === 'string' && ISO_DATE_RE.test(value)) {
    return formatToCustomDateString(
      value,
      agencyTimezone,
      'ddd, M/D/YY, h:mm A',
      language,
    );
  }
  if (typeof value === 'string') {
    // Entity-attribute values can carry rich HTML (e.g. a parsed resume) — show
    // readable text, not raw markup. No-op for plain strings.
    return htmlToPlainText(value);
  }
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  if (typeof value === 'object') {
    // Python Decimal serialization: { __reduce__: [{ "py/type": "decimal.Decimal" }, ["30.00"]] }
    const obj = value as Record<string, unknown>;
    if (Array.isArray(obj.__reduce__) && Array.isArray(obj.__reduce__[1])) {
      const numStr = obj.__reduce__[1][0];
      if (typeof numStr === 'string') {
        const n = Number(numStr);
        return Number.isFinite(n) ? n.toLocaleString(language) : numStr;
      }
    }
    return JSON.stringify(value);
  }
  return String(value);
}
